import React, { useEffect, useMemo, useState } from "react";
import { Modal, Form, Button } from "react-bootstrap";
import Select, { components, type ValueContainerProps } from "react-select";
import { useScopeTypes } from "../../../hooks/useScopeTypes";
import { useRoleOptions } from "../../../hooks/useRoleOptions";
import { useScopeIds } from "../../../hooks/useScopeIds";
import { useDebounce } from "../../../hooks/useDebounce";
import { useAssignRoleModal, type AssignRoleUser } from "./useAssignRoleModal";
import "./AssignRoleModal.css";

export type { AssignRoleUser };

type ScopeIdOption = { label: string; value: string };

const ScopeIdValueContainer = (props: ValueContainerProps<ScopeIdOption, true>) => {
  const { getValue, hasValue, children } = props;
  const values = getValue();

  if (!hasValue || !Array.isArray(values) || values.length === 0) {
    return <components.ValueContainer {...props}>{children}</components.ValueContainer>;
  }

  const first = values[0];
  const extraCount = values.length - 1;

  return (
    <components.ValueContainer {...props}>
      <span>{first.label}</span>
      {extraCount > 0 && (
        <span className="add-user-multi-summary"> ... +{extraCount}</span>
      )}
      {children}
    </components.ValueContainer>
  );
};

interface AssignRoleModalProps {
  show: boolean;
  onHide: () => void;
  user?: AssignRoleUser | null;
  onSuccess?: () => void;
}

interface AssignmentBlockProps {
  assignment: {
    assignmentId?: string;
    roleCode: string;
    scopeType: string;
    scopeIds: string[];
    academyId?: string;
  };
  index: number;
  scopeTypeOptions: { label: string; value: string }[];
  roleOptions: { label: string; value: string }[];
  rolesLoading: boolean;
  assignmentsLength: number;
  functions: ReturnType<typeof useAssignRoleModal>["functions"];
  onRoleSearchChange: (value: string) => void;
  scopeIdSearch: string;
  onScopeIdSearchChange: (value: string) => void;
}

const AssignmentBlock: React.FC<AssignmentBlockProps> = ({
  assignment,
  index,
  scopeTypeOptions,
  roleOptions,
  rolesLoading,
  assignmentsLength,
  functions,
  onRoleSearchChange,
  scopeIdSearch,
  onScopeIdSearchChange,
}) => {
  const { scopeIdOptions } = useScopeIds(
    assignment.scopeType,
    undefined,
    scopeIdSearch
  );

  const [persistedScopeIdLabels, setPersistedScopeIdLabels] = useState<
    Record<string, string>
  >({});

  useEffect(() => {
    if (!scopeIdOptions.length) return;
    setPersistedScopeIdLabels((prev) => {
      const next = { ...prev };
      for (const opt of scopeIdOptions) {
        if (assignment.scopeIds.includes(opt.value)) {
          next[opt.value] = opt.label;
        }
      }
      return next;
    });
  }, [scopeIdOptions, assignment.scopeIds]);

  useEffect(() => {
    setPersistedScopeIdLabels((prev) => {
      const next: Record<string, string> = {};
      for (const id of assignment.scopeIds) {
        if (id && prev[id]) next[id] = prev[id];
      }
      return next;
    });
  }, [assignment.scopeIds, assignment.scopeType]);

  const mergedScopeIdOptions = useMemo((): ScopeIdOption[] => {
    const labels: Record<string, string> = { ...persistedScopeIdLabels };
    for (const opt of scopeIdOptions) {
      if (assignment.scopeIds.includes(opt.value)) {
        labels[opt.value] = opt.label;
      }
    }
    const byValue = new Map<string, ScopeIdOption>();
    for (const opt of scopeIdOptions) {
      byValue.set(opt.value, opt);
    }
    for (const id of assignment.scopeIds) {
      if (!id || byValue.has(id)) continue;
      byValue.set(id, { value: id, label: labels[id] || id });
    }
    return Array.from(byValue.values());
  }, [
    scopeIdOptions,
    assignment.scopeIds,
    persistedScopeIdLabels,
  ]);

  const scopeIdValue = useMemo((): ScopeIdOption[] => {
    return assignment.scopeIds
      .map((id) => mergedScopeIdOptions.find((o) => o.value === id))
      .filter((o): o is ScopeIdOption => Boolean(o));
  }, [assignment.scopeIds, mergedScopeIdOptions]);

  return (
    <div className="assign-role-block">
      <div className="assign-role-block-header">
        <span className="assign-role-block-title">
          {assignment.roleCode || "New Role"}
        </span>
        {assignmentsLength > 1 && (
          <button
            type="button"
            className="assign-role-block-remove"
            onClick={() => functions.handleRemoveAssignment(index)}
            aria-label="Remove role assignment"
          >
            ×
          </button>
        )}
      </div>
      <div className="assign-role-field-row two-column-row">
        <Form.Group className="assign-role-field" style={{ flex: 1 }}>
          <Form.Label>
            Select Role <span className="required">*</span>
          </Form.Label>
          <Select
            classNamePrefix="react-select"
            value={
              roleOptions.find((opt) => opt.value === assignment.roleCode) ??
              null
            }
            onChange={(option) =>
              functions.handleRoleChange(index, option?.value ?? "")
            }
            options={roleOptions}
            placeholder="Select Role"
            isLoading={rolesLoading}
            onInputChange={(input, meta) => {
              if (meta.action === "input-change") {
                onRoleSearchChange(input);
              }
            }}
            filterOption={null}
            isClearable={false}
            menuPortalTarget={document.body}
            menuPosition="fixed"
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
          />
        </Form.Group>
        <Form.Group className="assign-role-field" style={{ flex: 1 }}>
          <Form.Label>Select Scope Type</Form.Label>
          <Select
            classNamePrefix="react-select"
            value={
              assignment.scopeType
                ? scopeTypeOptions.find(
                    (opt) => opt.value === assignment.scopeType
                  ) ?? null
                : null
            }
            onChange={(option) =>
              functions.handleScopeTypeChange(index, option?.value ?? "")
            }
            options={scopeTypeOptions}
            placeholder="Select scope type"
            isClearable
            menuPortalTarget={document.body}
            menuPosition="fixed"
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
          />
        </Form.Group>
      </div>
      <div className="assign-role-field assign-role-field-row">
        <div style={{ flex: 1 }}>
          <Form.Label>Select Scope ID(s)</Form.Label>
          <Select
            classNamePrefix="react-select"
            isMulti
            components={{
              ValueContainer: ScopeIdValueContainer,
              MultiValue: () => null,
            }}
            hideSelectedOptions={false}
            value={scopeIdValue}
            options={mergedScopeIdOptions}
            onChange={(selectedOptions) =>
              {
                const selected = selectedOptions ?? [];
                setPersistedScopeIdLabels((prev) => {
                  const next: Record<string, string> = { ...prev };
                  for (const opt of selected) {
                    next[opt.value] = opt.label;
                  }
                  for (const key of Object.keys(next)) {
                    if (!selected.some((o) => o.value === key)) {
                      delete next[key];
                    }
                  }
                  return next;
                });
                functions.handleScopeIdsChange(
                  index,
                  selected.map((opt) => opt.value)
                );
                onScopeIdSearchChange("");
              }
            }
            placeholder="Select scope IDs"
            closeMenuOnSelect={true}
            menuPortalTarget={document.body}
            menuPosition="fixed"
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
            onInputChange={(input, meta) => {
              if (meta.action === "input-change") {
                onScopeIdSearchChange(input);
              }
            }}
            onMenuClose={() => onScopeIdSearchChange("")}
            onBlur={() => onScopeIdSearchChange("")}
            filterOption={null}
          />
        </div>
      </div>
    </div>
  );
};

const AssignRoleModal: React.FC<AssignRoleModalProps> = ({
  show,
  onHide,
  user,
  onSuccess,
}) => {
  const { scopeTypeOptions } = useScopeTypes();
  const [roleSearch, setRoleSearch] = useState("");
  const [scopeIdSearch, setScopeIdSearch] = useState("");
  const debouncedRoleSearch = useDebounce(roleSearch, 600);
  const debouncedScopeIdSearch = useDebounce(scopeIdSearch, 600);
  const { roleOptions, loading: rolesLoading } = useRoleOptions(
    debouncedRoleSearch,
    show
  );
  const { functions, values, loading } = useAssignRoleModal({
    show,
    onHide,
    user,
    onSuccess,
  });
  const assignments = values.assignments;

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      className="assign-role-modal"
      dialogClassName="assign-role-modal-dialog"
      contentClassName="assign-role-modal-content"
    >
      <Modal.Body className="assign-role-modal-body">
        <h3 className="assign-role-modal-title">Assign Role</h3>
        <div className="assign-role-modal-scroll">
          <div className="assign-role-modal-fields">
            {assignments.map((assignment, index) => (
              <AssignmentBlock
                key={index}
                assignment={assignment}
                index={index}
                scopeTypeOptions={scopeTypeOptions}
                roleOptions={roleOptions}
                rolesLoading={rolesLoading}
                assignmentsLength={assignments.length}
                functions={functions}
                onRoleSearchChange={setRoleSearch}
                scopeIdSearch={debouncedScopeIdSearch}
                onScopeIdSearchChange={setScopeIdSearch}
              />
            ))}
          </div>
          <div className="assign-role-add-more">
            <Button
              variant="link"
              className="assign-role-add-btn"
              onClick={functions.handleAddAssignment}
              disabled={loading.isSubmitting}
            >
              + Assign New Role
            </Button>
          </div>
          {values.submitError && (
            <div className="text-danger small mb-2">
              {values.submitError}
            </div>
          )}
        </div>
        <div className="assign-role-modal-footer">
          <Button
            variant="outline-dark"
            className="assign-role-btn assign-role-btn-cancel"
            onClick={onHide}
            disabled={loading.isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            className="assign-role-btn assign-role-btn-confirm"
            onClick={functions.handleSubmit}
            disabled={loading.isSubmitting}
          >
            {loading.isSubmitting ? "Assigning..." : "Confirm"}
          </Button>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default AssignRoleModal;
